import { _isNumberValue } from '@angular/cdk/coercion';
import { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  type OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { type MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  DateService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { DialogPresets } from '@principle-theorem/ng-shared';
import { type IAppointmentSuggestion } from '@principle-theorem/principle-core/interfaces';
import {
  AppointmentSuggestion,
  type AppointmentSuggestionEntity,
  Event,
  TreatmentStep,
} from '@principle-theorem/principle-core';
import {
  CASUAL_DATE_FORMAT,
  TIME_FORMAT,
  snapshot,
  toMoment,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { BehaviorSubject, type Observable, Subject, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ContextDisplayComponent } from '../context-display/context-display.component';
import { AppointmentPermissions } from '@principle-theorem/principle-core/features';
import { AppointmentScoreType } from '../appointment-score/appointment-score.component';
import { SchedulingScenarioService } from '../../../scheduling-scenario.service';
import { AppointmentSchedulingFacade } from '@principle-theorem/ng-appointment/store';

@Component({
  selector: 'pr-appointment-options',
  templateUrl: './appointment-options.component.html',
  styleUrls: ['./appointment-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppointmentOptionsComponent implements OnDestroy {
  private _onDestroy$: Subject<void> = new Subject();
  private _sort: MatSort;
  displayedColumns$: Observable<string[]>;
  canDoubleBook$: Observable<boolean>;
  dataSource = new MatTableDataSource<AppointmentSuggestionEntity>();
  selection = new SelectionModel(false);
  selected$ = new BehaviorSubject<AppointmentSuggestionEntity | undefined>(
    undefined
  );

  readonly pageSizeOptions: number[] = [10, 25, 50, 100];
  readonly pageSize = 10;
  readonly dateFormat = CASUAL_DATE_FORMAT;
  readonly timeFormat = TIME_FORMAT;
  readonly appointmentScoreType = AppointmentScoreType;

  @Output() selectedChange = new EventEmitter<
    AppointmentSuggestionEntity | undefined
  >();

  @ViewChild(MatPaginator, { static: true })
  set paginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }

  @ViewChild(MatSort, { static: true })
  set matSort(ms: MatSort) {
    this._sort = ms;
    this._setSortingAttributes();
  }

  @Input()
  set options(options: AppointmentSuggestionEntity[]) {
    if (!options || !options.length) {
      this.dataSource.data = [];
      return;
    }
    this.dataSource.data = options;
  }

  get options(): AppointmentSuggestionEntity[] {
    return this.dataSource.data;
  }

  @Input()
  set selected(option: AppointmentSuggestionEntity | undefined) {
    this.selected$.next(option);
  }

  @Input() treatmentDuration: number;

  constructor(
    public dateService: DateService,
    private _organisation: OrganisationService,
    private _dialog: MatDialog,
    private _schedulingScenario: SchedulingScenarioService,
    private _schedulingFacade: AppointmentSchedulingFacade
  ) {
    this.displayedColumns$ = this._organisation.isSingleBrand$.pipe(
      map((isSingleBrand) => this._getDisplayedColumns(isSingleBrand))
    );

    this.canDoubleBook$ = this._organisation.userHasPermission$(
      AppointmentPermissions.SchedulingDoubleBook
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  showContext(suggestion: IAppointmentSuggestion): void {
    this._dialog.open(
      ContextDisplayComponent,
      DialogPresets.large({ data: suggestion, width: '80vw' })
    );
  }

  isBlocked(suggestion: IAppointmentSuggestion): boolean {
    return AppointmentSuggestion.isBlocked(suggestion);
  }

  distance(suggestion: IAppointmentSuggestion): string {
    return AppointmentSuggestion.distance(suggestion);
  }

  showScoreIcon(suggestion: IAppointmentSuggestion): boolean {
    return (
      suggestion.intersectingTags.length > 0 ||
      suggestion.schedulingConflicts.length > 0
    );
  }

  async toggle(
    option: AppointmentSuggestionEntity,
    emit: boolean = true,
    change: MatCheckboxChange
  ): Promise<void> {
    if (change.checked && this.isBlocked(option)) {
      const appointmentDetails = await snapshot(
        this._schedulingFacade.appointmentDetails$
      );

      const treatmentCategory = appointmentDetails.treatment
        ? TreatmentStep.defaultDisplayRef(
            appointmentDetails.treatment.step.display
          )
        : undefined;

      const isBlocked = await this._schedulingScenario.isBlockedByDoubleBooking(
        { event: option.event },
        treatmentCategory
      );

      if (isBlocked) {
        change.source.checked = false;
        return;
      }
    }

    const value = change.checked ? option : undefined;
    this.selected$.next(value);
    if (emit) {
      this.selectedChange.emit(value);
    }
  }

  durationShorterThanTreatment$(
    suggestion: IAppointmentSuggestion
  ): Observable<boolean> {
    const duration = Event.duration(suggestion.event);
    return of(duration < this.treatmentDuration);
  }

  isSelected(
    selected: AppointmentSuggestionEntity | undefined,
    option: AppointmentSuggestionEntity
  ): boolean {
    return selected?.uid === option.uid;
  }

  private _setSortingAttributes(): void {
    this.dataSource.sort = this._sort;
    this.dataSource.sortingDataAccessor = (
      data: IAppointmentSuggestion,
      sortHeaderId: string
    ): string | number => {
      switch (sortHeaderId) {
        case 'time':
          return Number(toMoment(data.event.from).format('HHmmss'));
        case 'date':
          return Number(toMoment(data.event.from).format('X'));
        case 'distance':
          return Number(moment().diff(toMoment(data.event.from)));
        case 'duration':
          return Number(Event.duration(data.event)) + data.score;
        case 'score':
          return data.score;
        default:
          const value = data[sortHeaderId as keyof IAppointmentSuggestion];
          return _isNumberValue(value) ? Number(value) : String(value);
      }
    };
  }

  private _getDisplayedColumns(isSingleBrand: boolean): string[] {
    const columns = [
      'select',
      'date',
      'time',
      'distance',
      'duration',
      'score',
      'practitioner',
    ];
    return isSingleBrand ? columns : [...columns, 'brand'];
  }
}
